import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { getApiEndpoint } from '@/utils/ERPUtils';
import { SETUP_STATUS_EVENT } from '@/utils/setupStatus';

export type SetupGateState = 'loading' | 'gated' | 'open';

/**
 * Route-level companion to the sidebar's visual setup lock.
 *
 * The sidebar only *disables* the nav buttons while a configuring admin's
 * workspace setup is incomplete, which a user can skip by editing the URL.
 * This hook exposes the same signal (`get_setup_status` -> can_configure &&
 * !complete) so a route wrapper can redirect every page except Customise
 * back to it, closing that bypass. The lock lifts automatically because the
 * Customise/provider-key/email saves broadcast SETUP_STATUS_EVENT, which we
 * listen for and re-evaluate.
 *
 * Fails OPEN: any error (or a non-configuring user) resolves to 'open' so a
 * broken check never traps a user out of the app.
 */
export const useSetupGate = (): SetupGateState => {
  const [state, setState] = useState<SetupGateState>('loading');
  const location = useLocation();

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(getApiEndpoint('GET_SETUP_STATUS'), { withCredentials: true });
      const s = res.data?.message;
      setState(s && s.can_configure && !s.complete ? 'gated' : 'open');
    } catch {
      setState('open'); // fail open - never trap the user on a broken check
    }
  }, []);

  // Re-check on mount and on every navigation, so a setup change re-engages the
  // gate on the next route even if the action that caused it forgot to
  // broadcast SETUP_STATUS_EVENT (belt-and-suspenders alongside the event).
  useEffect(() => {
    refresh();
  }, [refresh, location.pathname]);

  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener(SETUP_STATUS_EVENT, handler);
    return () => window.removeEventListener(SETUP_STATUS_EVENT, handler);
  }, [refresh]);

  return state;
};

/** The only route a configuring admin may reach until setup is complete. */
export const SETUP_GATE_ALLOWED_PATH = '/dashboard/admin/company';
